import React, {useCallback, useEffect, useState} from 'react';
import {Button, Checkbox, FormControlLabel} from '@mui/material';
import {useTableParams} from '../../core/hooks/table-params';
import Loader from '../../core/components/Loader';
import {IColumn, Table} from '../../core/components/Table';
import {Pagination} from '../../core/components/Pagination';
import {IContactUsRequest} from '../../core/interfaces';
import {getAllContactUsRequests} from '../../services/contact-us';
import {ContactUsActionModal} from './ContactUsActionModal';

const STATUS_COLORS: Record<string, string> = {
    NOT_CONTACTED: '#d32f2f',
    IN_PROGRESS: '#f57c00',
    CONTACTED: '#388e3c',
    SOLVED: '#9e9e9e',
}

const STATUS_LABELS: Record<string, string> = {
    NOT_CONTACTED: 'Not Contacted',
    IN_PROGRESS: 'In Progress',
    CONTACTED: 'Contacted',
    SOLVED: 'Solved',
}

export const ContactUsListPage: React.FC = (): JSX.Element => {
    const {page, setPage, size, setSize, totalElements, setTotalElements, sortBy, setSortBy} =
        useTableParams({defaultSortBy: 'createdAt'})

    const [loading, setLoading] = useState<boolean>(false)
    const [requests, setRequests] = useState<IContactUsRequest[]>([])
    const [selectedRequest, setSelectedRequest] = useState<IContactUsRequest | null>(null)
    const [includeSolved, setIncludeSolved] = useState<boolean>(false)

    const getData = useCallback(async () => {
        try {
            setLoading(true)
            const response = await getAllContactUsRequests({page, size, sortBy, includeSolved})
            setTotalElements(response.data.totalElements)
            setRequests(response.data.content)
            setPage(page + 1)
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }, [page, size, sortBy, includeSolved])

    useEffect(() => {
        getData()
    }, [page, size, sortBy, includeSolved])

    const handleSortBy = (sort: string) => {
        setSortBy(sort === sortBy ? 'createdAt' : sort)
    }

    const columns: IColumn[] = [
        {title: 'First Name', accessor: 'firstName', sortable: true, searchable: false},
        {title: 'Last Name', accessor: 'lastName', sortable: true, searchable: false},
        {title: 'Email', accessor: 'email', sortable: false, searchable: false},
        {title: 'Phone', accessor: 'phone', sortable: false, searchable: false},
        {title: 'City', accessor: 'city', sortable: false, searchable: false},
        {title: 'Institution', accessor: 'kindergarten', sortable: false, searchable: false},
        {title: 'Role', accessor: 'role', sortable: false, searchable: false},
        {
            title: 'Status',
            accessor: '',
            render: (row: IContactUsRequest) => (
                <span style={{
                    color: STATUS_COLORS[row.contactedStatus] ?? '#000',
                    fontWeight: 600,
                }}>
                    {STATUS_LABELS[row.contactedStatus] ?? row.contactedStatus}
                </span>
            ),
        },
        {
            title: 'Submitted',
            accessor: '',
            render: (row: IContactUsRequest) => (
                <span>{new Date(row.createdAt).toLocaleDateString('ro', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                })}</span>
            ),
        },
        {
            title: 'Action',
            accessor: '',
            render: (row: IContactUsRequest) => (
                <Button className="btn btn-secondary" onClick={() => setSelectedRequest(row)}>
                    Manage
                </Button>
            ),
        },
    ]

    return (
        <>
            <div style={{padding: 20, backgroundColor: '#F8F8F8', position: 'relative'}}>
                {loading && <Loader/>}
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20}}>
                    <h2>Contact Us Requests</h2>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={includeSolved}
                                onChange={(e) => setIncludeSolved(e.target.checked)}
                            />
                        }
                        label="Show Solved"
                    />
                </div>
                <div className="overflow-x-auto">
                    <Table
                        handleSearch={() => {}}
                        isCheck={[]}
                        columns={columns}
                        handleSortBy={handleSortBy}
                        data={requests}
                    />
                </div>
                <Pagination
                    size={size}
                    totalElements={totalElements}
                    setSize={setSize}
                    page={page + 1}
                    changePage={(newPage) => setPage(newPage)}
                />
            </div>
            {selectedRequest && (
                <ContactUsActionModal
                    request={selectedRequest}
                    onClose={() => setSelectedRequest(null)}
                    onSaved={() => {
                        setSelectedRequest(null)
                        getData()
                    }}
                />
            )}
        </>
    )
}
